import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useInProgressSessions } from '@hotjar/webapp/hooks/useInProgressSessions';
import { plural } from '@hotjar/webapp/lib/plural';
import { Icon, Text, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${tokens.spacer.x8};
`;

const progressLoaderAnimation = keyframes`
  0% {
    transform:  translateX(0) scaleX(0);
  }
  40% {
    transform:  translateX(0) scaleX(0.4);
  }
  100% {
    transform:  translateX(100%) scaleX(0.5);
  }
`;

const LOADER_COLORS = [
  tokens.BRAND_COLORS.mint_700,
  tokens.BRAND_COLORS.cobalt_700,
  tokens.BRAND_COLORS.ember_700,
];

const colorTransitionAnimation = keyframes`
${LOADER_COLORS.map(
  (colorValue, idx) => `${(100 / LOADER_COLORS.length) * idx}% {
    background-color:  ${colorValue};
  }`,
)}
`;

const TeaserBlockContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-bottom: 40px;
`;

const StyledIconWrapper = styled.span`
  display: inline-flex;
  position: relative;
  margin: 0 20px;

  :before {
    content: '';
    background: ${tokens.color.ui_active};
    width: 11px;
    height: 11px;
    display: block;
    position: absolute;
    border-radius: 100%;
    bottom: 0;
    right: 0;
    border: 2px solid white;
    z-index: 1;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${tokens.BRAND_COLORS.lilac_400};
  width: 30px;
  height: 30px;
`;

const TeaserBlock = styled.section`
  border-radius: ${tokens.radius.m};
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid ${tokens.color.ui_border_light};
  ${tokens.elevation(1)};
  width: 550px;
  justify-content: flex-start;
  padding: 20px 0;
  background: white;
  overflow: hidden;

  :before {
    content: '';
    width: 100%;
    height: 4px;
    background-color: ${tokens.BRAND_COLORS.cobalt_200};
    top: 0;
    left: 0;
    position: absolute;
  }

  :after {
    content: '';
    width: 100%;
    height: 4px;
    background-color: ${tokens.BRAND_COLORS.cobalt_600};
    animation: ${progressLoaderAnimation} 1.5s infinite linear,
      ${colorTransitionAnimation} ${1.5 * LOADER_COLORS.length}s infinite linear;
    transform-origin: 0% 50%;
    top: 0;
    left: 0;
    position: absolute;
  }
`;

const Block = styled.div`
  display: flex;
`;

const InfoBlock = styled(Block)`
  display: flex;
  flex-direction: column;
  p {
    margin: 0;
  }
`;

const Title = styled(Text)`
  margin: 0 0 ${tokens.spacer.x1};
`;

const Subtitle = styled(Text)`
  margin: 0 0 ${tokens.spacer.x3};
`;

const Bold = styled.span`
  font-weight: 600;
`;

const EllipsisWrapper = styled.span`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  direction: rtl;
  max-width: 450px;
  display: block;
`;

const LtrSpan = styled.span`
  unicode-bidi: embed;
  direction: ltr;
`;

const cleanUrl = (pageUrl: string): string => {
  const url = new URL(pageUrl);

  return url.hostname + url.pathname;
};

export const InProgressSessionsTeaser = ({ siteId }: { siteId: number }): JSX.Element | null => {
  const { t } = useTranslation();
  const pluralSessions = (count: number) => plural({ one: 'session', other: 'sessions' }, count);
  const inProgressSessions = useInProgressSessions({ siteId, selectRandom: true });
  if (!inProgressSessions.data) {
    return null;
  }

  const count = inProgressSessions.data.count || 0;

  if (count === 0) {
    return null;
  }

  if (!inProgressSessions.data.session) {
    return null;
  }

  const { browser, device, newUser, pageUrl, country } = inProgressSessions.data.session;

  return (
    <Wrapper>
      <Title as="h5" textType="header5" textColor="text_default">
        {t('activation:empty-states.pending-data.recordings.title')}
      </Title>
      <Subtitle as="p" textType="body1">
        Tracking{' '}
        <Text textType="body1" textColor="text_success" data-hj-allow>
          {count} active {pluralSessions(count)}
        </Text>{' '}
        right now on your site, including...
      </Subtitle>
      <TeaserBlockContainer>
        {Array(Math.min(count, 3))
          .fill('')
          .map((_, idx) => (
            <TeaserBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`teaserBlock-${idx}`}
              css={css`
                position: ${idx > 0 ? 'absolute' : 'relative'};
                z-index: ${idx > 0 ? idx * -1 : null};
                transform: ${idx > 0
                  ? `scale(${1 - (idx * 2) / 100}) translateY(${idx * 10}px)`
                  : null};
              `}
            >
              <Block>
                <StyledIconWrapper>
                  <StyledIcon icon="user_avatar" />
                </StyledIconWrapper>
              </Block>
              <InfoBlock>
                <Text>
                  A <Bold>{newUser ? 'new' : 'returning'} user</Bold> using <Bold>{browser}</Bold>{' '}
                  on a <Bold>{device}</Bold>
                  {country ? (
                    <>
                      , browsing from <Bold>{country}</Bold>
                    </>
                  ) : (
                    <> browsing</>
                  )}
                  :
                </Text>
                <Text as="p" textColor="text_light">
                  <EllipsisWrapper>
                    <LtrSpan>{cleanUrl(pageUrl)}</LtrSpan>
                  </EllipsisWrapper>
                </Text>
              </InfoBlock>
            </TeaserBlock>
          ))}
      </TeaserBlockContainer>
      <Subtitle as="p" textType="body1" textColor="text_light">
        You&apos;ll get an email as soon as the first one is ready
      </Subtitle>
    </Wrapper>
  );
};
